import { fetchWithAuth } from 'utils';
import { apiUrl } from '@/utils/constants';
import { SourceDefinition, Matcher, RagDocument, ChatMetadata } from '@/types';

type ChatConfig = Pick<ChatMetadata, 'lastMessageNeighbors' | 'conversationNeighbors'>;

export const ragApi = {
  getSources: async (chatId: string) => {
    const res = await fetchWithAuth(`${apiUrl}/rag/${chatId}/source-definitions`);
    if (!res.ok) throw new Error('Failed to fetch sources');
    return res.json() as Promise<SourceDefinition[]>;
  },

  createSource: async (chatId: string) => {
    const res = await fetchWithAuth(`${apiUrl}/rag/${chatId}/source-definitions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'TEXT',
        frequency: 'NONE',
        chunkSize: 1250,
        chunksOverlap: 600,
      }),
    });
    if (!res.ok) throw new Error('Failed to create source');
    return res.json() as Promise<SourceDefinition>;
  },

  updateSource: async (source: SourceDefinition) => {
    const res = await fetchWithAuth(`${apiUrl}/rag/source-definitions/${source.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: source.type,
        url: source.url,
        linkSelector: source.linkSelector,
        frequency: source.frequency,
        chunkSize: Number(source.chunkSize),
        chunksOverlap: Number(source.chunksOverlap),
        splitDelimiter: source.splitDelimiter,
      }),
    });
    if (!res.ok) throw new Error('Failed to save source');
    return res.json() as Promise<SourceDefinition>;
  },

  deleteSource: async (id: number) => {
    const res = await fetchWithAuth(`${apiUrl}/rag/source-definitions/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok && res.status !== 204) throw new Error('Failed to delete source');
  },

  createMatcher: async (sourceId: number) => {
    const res = await fetchWithAuth(`${apiUrl}/rag/source-definitions/${sourceId}/matchers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'CSS_SELECTOR', valueFrom: 'body' }),
    });
    if (!res.ok) throw new Error('Failed to create matcher');
    return res.json() as Promise<Matcher>;
  },

  updateMatcher: async (matcher: Matcher) => {
    const res = await fetchWithAuth(`${apiUrl}/rag/matchers/${matcher.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: matcher.type, valueFrom: matcher.valueFrom }),
    });
    if (!res.ok) throw new Error('Failed to save matcher');
    return res.json() as Promise<Matcher>;
  },

  deleteMatcher: async (id: number) => {
    const res = await fetchWithAuth(`${apiUrl}/rag/matchers/${id}`, { method: 'DELETE' });
    if (!res.ok && res.status !== 204) throw new Error('Failed to delete matcher');
  },

  getDocuments: async (sourceId: number, skip = 0) => {
    const res = await fetchWithAuth(
      `${apiUrl}/rag/source-definitions/${sourceId}/documents?take=20&skip=${skip}`
    );
    if (!res.ok) throw new Error('Failed to fetch documents');
    return res.json() as Promise<{ data: RagDocument[]; meta: { total: number } }>;
  },

  getChatConfig: async (chatId: string) => {
    const res = await fetchWithAuth(`${apiUrl}/chat-metadata/${chatId}`);
    if (!res.ok) throw new Error('Failed to fetch chat config');
    return res.json() as Promise<ChatConfig>;
  },

  updateChatConfig: async (chatId: string, config: ChatConfig) => {
    const res = await fetchWithAuth(`${apiUrl}/chat-metadata/${chatId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    if (!res.ok) throw new Error('Failed to update chat config');
    return res.json();
  },
};
